/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path configuration
 * @description Common UI configuration
 */
import React, { Suspense } from "react";
import { Route, useRouteMatch } from "react-router-dom";
import { useAppContext } from "./../context/AppCtxProvider";
import { LanguageSwitchView } from "./../languageswitch/index";
import BreadcrumbsMain from "./../ui/Breadcrumbs";
import PlanSwitchButton from "./../ui/PlanSwitchButton";
import SearchBar from "./../ui/components/SearchBar";
import { getFileApiURL } from "./../utils/url-utils";
import { AppBarImage } from "./../viewcomponents/QPRAppBar/index";
import {
  ChartProps,
  ICardElementWithChildren,
  IChartSettings,
  IGaugeSettings,
  IPerformanceTableSettings,
  ITargetCardSettings,
  IValueTableSettings,
  TCompletedWorkflowsViewSettings,
  TExecutiveDashboardViewSettings,
  THierarchyMode,
  TInitiativeDetailsViewSettings,
  TKPIDetailsViewSettings,
  TKPIEditingViewSettings,
  TKPIPopupViewSettings,
  TKPIsAndInitiativesViewSettings,
  TKPIsAndProjectsManagementViewSettings,
  TKPIsViewSettings,
  TMyStoriesViewSettings,
  TMyTasksViewSettings,
  TNavItem,
  TOverviewDashboardViewSettings,
  TProjectEditingViewSettings,
  TSelectablePeriods,
  TSettingsBase,
  TStrategyMapViewSettings,
  TStrategyReportSettings,
  TValueSettings,
  TViewSettings,
  TWorkflowInputViewSettings,
} from "./types";

export const System = {
  metadata: {} as any,
  views: {
    kpiDetailsView: {} as TKPIDetailsViewSettings,
    kpisAndInitiativesView: {} as TKPIsAndInitiativesViewSettings,
    kpisView: {} as TKPIsViewSettings,
    executiveDashboardView: {} as TExecutiveDashboardViewSettings,
    initiativeDetailsView: {} as TInitiativeDetailsViewSettings,
    strategyMapView: {} as TStrategyMapViewSettings,
    overviewDashboardView: {} as TOverviewDashboardViewSettings,
    kpiPopupView: {} as TKPIPopupViewSettings,
    myStoriesView: {} as TMyStoriesViewSettings,
    kpisAndProjectsManagementView: {} as TKPIsAndProjectsManagementViewSettings,
    kpiEditingView: {} as TKPIEditingViewSettings,
    projectEditingView: {} as TProjectEditingViewSettings,
    completedWorkflowsView: {} as TCompletedWorkflowsViewSettings,
    strategyReportSettings: {} as TStrategyReportSettings,
    myTasksView: {} as TMyTasksViewSettings,
    workflowInputView: {} as TWorkflowInputViewSettings,
  },
  settings: {
    models: [] as string[],
    languages: [] as string[],
    defaultModel: "",
    defaultPeriod: "",
  },
  query: {
    enableObjectCache: true,
    enableChildCache: true,
    logQueries: false,
    cacheExpireTime: 1000 * 60 * 30,
  },
  flags: {
    mobileAppRegistration: false,
  },
  search: {
    searchQuery: (modelId: string, searchKeyword: string, period: string) => {
      return String.raw`[${modelId}].subobjects(recursive=1, criteria="hasreferencescorecard=\"0\" AND Find(\"${searchKeyword}\", LOWER(measure.value(series=\"JS_ATTR\", period=\"Q1 / 2010\")))")`;
    },
    searchAttributes: (period: string) => {
      return String.raw`name, symbol, rangevalue, typename, value(period="${period}",as="performance"), rangecolor(period="${period}"), value(series="JS_ATTR", period="firstforseries"), related(relation=parentObjects, as="parent_id")`;
    },
    searchFilter: (elem: any) => {
      return true;
    },
    searchElementTypes: {
      Government: 5,
      Theme: 5,
      Cluster: 4,
      Aim: 3,
      Priority: 3,
      KPI: 2,
      Project: 2,
    },
  },
  charts: {
    gaugeConfiguration: (options: ChartProps): any => {
      return {};
    },
    barChartConfiguration: (options: ChartProps): any => {
      return {};
    },
    singleKPIDonutConfiguration: (options: ChartProps): any => {
      return {};
    },
    multiDonutChartConfiguration: (options: ChartProps): any => {
      return {};
    },
  },
  routing: {
    typeToPageName: (typename: string) => {
      return typename;
    },
  },
  Toolbar: {
    Breadcrumb: () => {
      const { path } = useRouteMatch();
      const ctx = useAppContext();
      return (
        <Route
          path={`${path}/${ctx.modelId}/:id/:meaId?/:period?/`}
          component={BreadcrumbsMain}
        />
      );
    },
    PlanSwitch: () => {
      const { path } = useRouteMatch();
      return (
        <Route
          path={`${path}/:model/landingpage`}
          component={PlanSwitchButton}
        />
      );
    },
    SearchBar: () => <SearchBar />,
    LanguageSwitchView: () => (
      <Suspense fallback={<div>Switching language...</div>}>
        <LanguageSwitchView />
      </Suspense>
    ),
  },
  icons: {
    AppBarImage: <AppBarImage />,
    UserAvatar: (<div>User Avatar</div>) as JSX.Element,
    NotificationIcon: (
      <img
        src={getFileApiURL("/icons/NotificationCenter.svg")}
        alt="Notifications"
      />
    ),
  },
  mapNavitems: (list: TNavItem) => list,
  mapQueryResults: (row: any) => row,
  draftValuesHandling: {
    isDraftStatus: (value: string | undefined) => {
      return value !== "1";
    },
    getListOfDraftFilteredSeries: (typename: string) => {
      switch (typename.toLocaleLowerCase()) {
        case "kpi":
          return ["ACT", "ACT_YTD", "PERF", "PERF_YTD", "STR_PERF"];
        case "sub-kpi":
          return [];
        case "initiative":
          return [
            "PLANNED_COMPLETION",
            "ACTUAL_START_DATE",
            "ACTUAL_END_DATE",
            "EXPENDITURE",
            "COMPLETION_PERCENTAGE",
            "SLIPPAGE",
            "VARIANCE",
            "PERP",
            "BUDGET_UTIL",
          ];
        case "milestone":
          return [];
        case "benchmark":
          return [];
        default:
      }
      return [
        "PERF",
        "EXPENDITURE",
        "PERF_PROJECTS",
        "PERF_KPIS",
        "BUDGET_UTIL",
      ];
    },
    disabledPages: [
      "acceptmilestone",
      "acceptmsworkflowlogs",
      "acceptmsworkflowsteps",
      "kpisapproval",
      "kpiworkflowsteps",
      "kpiworkflowlogs",
      "kpisandprojectsmanagement",
      "kpisform",
      "projectsform",
      "workflowlogs",
      "workflowsteps",
      "mytasks",
      "completedtasks",
      "workflowinput",
      "workflowsform",
      "workflowsformedit",
      "usersform",
      "groupsform",
      "rolesform",
      "rolesformedit",
      "admintools",
    ],
  },
};
export const setFlag = (name: keyof typeof System.flags, value: boolean) => {
  System.flags[name] = value;
};
export const setIcon = (
  name: keyof typeof System.icons,
  value: JSX.Element
) => {
  System.icons[name] = value;
};
export const setToolbar = (
  name: keyof typeof System.Toolbar,
  value: () => JSX.Element
) => {
  System.Toolbar[name] = value;
};
export const setChartSetting = (
  name: keyof typeof System.charts,
  value: any
) => {
  System.charts[name] = value;
};

export const setViewSettings = (viewSettings: TViewSettings) => {
  if (!viewSettings) return;

  if (viewSettings.kpiDetailsView)
    Object.assign(System.views.kpiDetailsView, viewSettings.kpiDetailsView);
  if (viewSettings.initiativeDetailsView)
    Object.assign(
      System.views.initiativeDetailsView,
      viewSettings.initiativeDetailsView
    );
  if (viewSettings.kpisView)
    Object.assign(System.views.kpisView, viewSettings.kpisView);
  if (viewSettings.executiveDashboardView)
    Object.assign(
      System.views.executiveDashboardView,
      viewSettings.executiveDashboardView
    );
  if (viewSettings.strategyMapView)
    Object.assign(System.views.strategyMapView, viewSettings.strategyMapView);
  if (viewSettings.overviewDashboardView)
    Object.assign(
      System.views.overviewDashboardView,
      viewSettings.overviewDashboardView
    );
  if (viewSettings.kpisAndInitiativesView)
    Object.assign(
      System.views.kpisAndInitiativesView,
      viewSettings.kpisAndInitiativesView
    );
  if (viewSettings.kpiPopupView)
    Object.assign(System.views.kpiPopupView, viewSettings.kpiPopupView);
  if (viewSettings.myStoriesView)
    Object.assign(System.views.myStoriesView, viewSettings.myStoriesView);
  if (viewSettings.kpisAndProjectsManagementView)
    Object.assign(
      System.views.kpisAndProjectsManagementView,
      viewSettings.kpisAndProjectsManagementView
    );
  if (viewSettings.kpiEditingView) {
    Object.assign(System.views.kpiEditingView, viewSettings.kpiEditingView);
  }
  if (viewSettings.projectEditingView) {
    Object.assign(
      System.views.projectEditingView,
      viewSettings.projectEditingView
    );
  }
  if (viewSettings.completedWorkflowsView)
    Object.assign(
      System.views.completedWorkflowsView,
      viewSettings.completedWorkflowsView
    );
  if (viewSettings.myTasksView)
    Object.assign(System.views.myTasksView, viewSettings.myTasksView);
  if (viewSettings.workflowInputView)
    Object.assign(
      System.views.workflowInputView,
      viewSettings.workflowInputView
    );
  if (viewSettings.strategyReportSettings)
    Object.assign(
      System.views.strategyReportSettings,
      viewSettings.strategyReportSettings
    );
};

const getActiveSettings = (
  element: ICardElementWithChildren,
  settings: TSettingsBase[] | undefined
) => {
  if (!settings) return undefined;

  // find settings with anchor
  let set = settings.find((s) => {
    return (
      (s.anchor?.measureId && s.anchor?.measureId === element.id) ||
      (s.anchor?.valueSettingsName &&
        s.anchor?.valueSettingsName === element.valuesettingsname) ||
      (s.anchor?.parentId && s.anchor?.parentId === element.parent_element_id)
    );
  });
  if (set) return set;

  // find first non anchored setting
  return settings.find((setting) => !setting.anchor);
};

export const getInitiativeDetailsViewChartSettings = (
  element: ICardElementWithChildren
): IChartSettings => {
  const defaultSettings = {
    series: [
      {
        symbol: "PLANNED_COMPLETION",
        color: "rgb(93,98,104)",
      },
      {
        symbol: "COMPLETION_PERCENTAGE",
        color: "rgba(38, 166, 242, 0.8)",
      },
    ],
  };

  const chartSettings = getActiveSettings(
    element,
    System.views.strategyReportSettings.initiativeChartSettings?.chartSettings
  );
  if (chartSettings)
    return Object.assign(defaultSettings, chartSettings as IChartSettings);

  // no config found - use defaults
  return defaultSettings;
};

export const getKPIDetailsViewChartSettings = (
  element: ICardElementWithChildren
): IChartSettings => {
  const defaultSettings = {
    series: [
      {
        symbol: "ACT",
        color: "rgba(38, 166, 242, 0.8)",
      },
      {
        symbol: "TAR",
        color: "rgb(52, 182, 177)",
      },
      {
        symbol: "ALA",
        color: "rgb(200, 82, 84)",
      },
    ],
  };

  const chartSettings = getActiveSettings(
    element,
    System.views.kpiDetailsView.chartSettings
  );
  if (chartSettings)
    return Object.assign(defaultSettings, chartSettings as IChartSettings);

  // no config found - use defaults
  return defaultSettings;
};

export const getKPIDetailsViewTargetCardSettings = (
  element: ICardElementWithChildren
): ITargetCardSettings => {
  const defaultSettings = {
    title: "kpis.Year End Target",
    value: { attribute: "TAR_YTD_valuewithoutsymbol" },
    unit: { attribute: "TAR_YTD_unit" },
  };

  const targetCardSettings = getActiveSettings(
    element,
    System.views.kpiDetailsView.targetCardSettings
  );
  if (targetCardSettings)
    return Object.assign(
      defaultSettings,
      targetCardSettings as ITargetCardSettings
    );

  // no config found - use defaults
  return defaultSettings;
};

export const getKPIValueTableSettings = (
  element: ICardElementWithChildren
): IValueTableSettings => {
  let valueTableSettings = getActiveSettings(
    element,
    System.views.kpiDetailsView.valueTableSettings
  ) as IValueTableSettings | undefined;
  if (!valueTableSettings) {
    // no settings defined, use defaults
    valueTableSettings = {
      periodsBeforeActual: undefined,
      periodsAfterActual: undefined,
      series: [
        { symbol: "ACT" },
        { symbol: "TAR" },
        { symbol: "PERF", showRangeColors: true },
        { symbol: "ACT_YTD" },
        { symbol: "TAR_YTD" },
        { symbol: "PERF_YTD", showRangeColors: true },
      ],
    };
  }

  // use chart periodsettings by default for valuetable
  const chartSettings = getActiveSettings(
    element,
    System.views.kpiDetailsView.chartSettings
  ) as IChartSettings | undefined;
  if (
    typeof valueTableSettings.periodsBeforeActual === "undefined" &&
    typeof chartSettings?.periodsBeforeActual === "number"
  )
    valueTableSettings.periodsBeforeActual = chartSettings.periodsBeforeActual;
  if (
    typeof valueTableSettings.periodsAfterActual === "undefined" &&
    typeof chartSettings?.periodsAfterActual === "number"
  )
    valueTableSettings.periodsAfterActual = chartSettings.periodsAfterActual;

  return valueTableSettings;
};

export const getKPIPerformanceTableSettings = (
  element: ICardElementWithChildren
): IPerformanceTableSettings => {
  const valueTableSettings = getActiveSettings(
    element,
    System.views.strategyReportSettings.kpiPerformanceTableSettings
  );
  if (valueTableSettings) return valueTableSettings;

  // no config for KPI valuetable - use defaults
  return {
    series: [
      {
        symbol: "PERF_YTD",
        cellType: "percentageBar",
        colorAttribute: "rangecolor",
      },
      { symbol: "ACT_YTD" },
      { symbol: "TAR_YTD" },
      { symbol: "W" },
      { symbol: "PERF_W_YTD" },
    ],
  };
};

export const getInitiativePerformanceTableSettings = (
  element: ICardElementWithChildren
): IPerformanceTableSettings => {
  const valueTableSettings = getActiveSettings(
    element,
    System.views.strategyReportSettings.initiativePerformanceTableSettings
  );
  if (valueTableSettings) return valueTableSettings;

  // no config for KPI valuetable - use defaults
  return {
    series: [
      {
        symbol: "COMPLETION_PERCENTAGE",
        cellType: "percentageBar",
        colorAttribute: "rangecolor",
      },
      { symbol: "PLANNED_COMPLETION" },
      {
        symbol: "VARIANCE",
        cellType: "showRangeColor",
        colorAttribute: "rangecolor",
      },
    ],
  };
};

export const getKPIPopupSeriesSettings = (): string[] => {
  if (!System.views.kpiPopupView.series)
    return ["ACT_YTD", "TAR_YTD", "PERF_YTD"];
  return System.views.kpiPopupView.series;
};

export const getMyStoriesSelectablePeriodsSettings = (): TSelectablePeriods => {
  if (!System.views.myStoriesView.selectablePeriods)
    return { firstYear: "2022", lastYear: "2024" };
  return System.views.myStoriesView.selectablePeriods;
};

export const getKpisAndProjectsManagementHierarchyMode = (): THierarchyMode => {
  if (!System.views.kpisAndProjectsManagementView.hierarchyMode)
    return "Scorecards";
  return System.views.kpisAndProjectsManagementView.hierarchyMode;
};

export const getKpiEditingViewValueSettings = (): TValueSettings => {
  if (!System.views.kpiEditingView.valueSettings)
    return [
      {
        name: "KPI Minimize",
        series: [{ symbol: "ACT" }, { symbol: "TAR" }],
      },
      {
        name: "KPI Maximize",
        series: [{ symbol: "ACT" }, { symbol: "TAR" }],
      },
    ];
  return System.views.kpiEditingView.valueSettings;
};

export const getProjectEditingViewVisibleUnits = (): string[] | undefined => {
  if (!System.views.projectEditingView.visibleUnits)
    return ["USD", "AED", "Thousand AED", "Million AED"];
  return System.views.projectEditingView.visibleUnits;
};

export const getIniativeDetailsSlideGaugeSettings = (
  element: ICardElementWithChildren
): IGaugeSettings => {
  const defaultSettings = {
    title: "Progress",
    colorAttribute: "rangecolor",
    valueAttribute: "COMPLETION_PERCENTAGE",
    unit: { value: "%" },
    minValue: { value: 0 },
    maxValue: { value: 100 },
    fields: [
      {
        title: "Variance",
        attribute: "VARIANCE_prettyvalue",
      },
      {
        title: "Planned Progress",
        attribute: "PLANNED_COMPLETION_prettyvalue",
      },
      {
        title: "YTD Expenditure",
        attribute: "EXPENDITURE_prettyvalue",
      },
      {
        title: "Total budget",
        attribute: "BUDGET_prettyvalue",
      },
      {
        title: "Progress",
        attribute: "COMPLETION_PERCENTAGE",
      },
    ],
  };
  const gaugeSettings = getActiveSettings(
    element,
    System.views.strategyReportSettings.initiativeGaugeSettings
  );
  if (gaugeSettings) return Object.assign(defaultSettings, gaugeSettings);

  return defaultSettings;
};

export const getKPIDetailsSlideGaugeSettings = (
  element: ICardElementWithChildren
): IGaugeSettings => {
  const defaultSettings = {
    title: "Target Achievement YTD",
    colorAttribute: "rangecolor",
    valueAttribute: "PERF_YTD",
    unit: { value: "%" },
    minValue: { value: 0 },
    maxValue: { value: 100 },
    fields: [
      {
        title: "Actual",
        attribute: "ACT_prettyvalue",
      },
      {
        title: "Target",
        attribute: "TAR_prettyvalue",
      },
      {
        title: "Target YTD",
        attribute: "TAR_YTD_prettyvalue",
      },
    ],
  };
  const gaugeSettings = getActiveSettings(
    element,
    System.views.strategyReportSettings.kpiGaugeSettings
  );
  if (gaugeSettings) return Object.assign(defaultSettings, gaugeSettings);

  // no config for KPI gauge - use defaults
  return defaultSettings;
};

export const getKPIDetailsViewGaugeSettings = (
  element: ICardElementWithChildren
): IGaugeSettings => {
  const defaultSettings = {
    title: "Target Achievement YTD",
    colorAttribute: "rangecolor",
    valueAttribute: "PERF_YTD",
    unit: { value: "%" },
    minValue: { value: 0 },
    maxValue: { value: 100 },
    fields: [
      {
        title: "Score",
        attribute: "SCORE",
      },
      {
        title: "Actual YTD",
        attribute: "ACT_YTD_prettyvalue",
      },
      {
        title: "Target YTD",
        attribute: "TAR_YTD_prettyvalue",
      },
    ],
  };
  const gaugeSettings = getActiveSettings(
    element,
    System.views.kpiDetailsView.gaugeSettings
  );
  if (gaugeSettings) return Object.assign(defaultSettings, gaugeSettings);

  // no config for KPI gauge - use defaults
  return defaultSettings;
};

export const getInitiativeDetailsViewGaugeSettings = (
  element: ICardElementWithChildren
): IGaugeSettings => {
  const defaultSettings = {
    title: "initiatives.Budget Utilization",
    colorAttribute: "rangecolor",
    valueAttribute: "BUDGET_UTIL_valuewithoutsymbol",
    unit: { attribute: "BUDGET_UTIL_unit" },
    minValue: { value: 0 },
    maxValue: { value: 100 },
    fields: [
      {
        title: "initiatives.Total Budget",
        attribute: "BUDGET_prettyvalue",
      },
      {
        title: "initiatives.YTD Expenditure",
        attribute: "EXPENDITURE_prettyvalue",
      },
    ],
  };
  const gaugeSettings = getActiveSettings(
    element,
    System.views.initiativeDetailsView.gaugeSettings
  );
  if (gaugeSettings) return Object.assign(defaultSettings, gaugeSettings);

  // no config found - use defaults
  return defaultSettings;
};

export const getKPIsViewGaugeSettings = (
  element: ICardElementWithChildren
): IGaugeSettings => {
  const defaultSettings = {
    title: "Target Achievement YTD",
    colorAttribute: "rangecolor",
    valueAttribute: "PERF_YTD",
    unit: { value: "%" },
    minValue: { value: 0 },
    maxValue: { value: 100 },
    fields: [
      {
        title: "Actual YTD",
        attribute: "ACT_YTD_prettyvalue",
      },
      {
        title: "Target YTD",
        attribute: "TAR_YTD_prettyvalue",
      },
	{
		title: "Score",
		attribute: "SCORE",
	},
    ],
  };

  const gaugeSettings = getActiveSettings(
    element,
    System.views.kpisView.gaugeSettings
  );
  if (gaugeSettings) return Object.assign(defaultSettings, gaugeSettings);

  // no config for KPI gauge - use defaults
  return defaultSettings;
};

export const getExecutiveDashboardViewSettings =
  (): TExecutiveDashboardViewSettings => {
    return System.views.executiveDashboardView;
  };

export const getStrategyReportSettings = (): TStrategyReportSettings => {
  const defaultSettings = {
    hideOverallPerformanceSummary: false,
    hideKPIPerformanceSummary: false,
    hideKPIPerformanceTable: false,
    hideKPIPerformanceDetails: false,
    hideInitiativePerformanceSummary: false,
    hideInitiativePerformanceTable: false,
    hideInitiativePerformanceDetails: false,
  };

  const reportSettings = System.views.strategyReportSettings;
  if (reportSettings) return Object.assign(defaultSettings, reportSettings);

  return defaultSettings;
};

export const getExecutiveDashboardViewGaugeSettings = (
  element: ICardElementWithChildren
): IGaugeSettings => {
  const defaultSettings = {
    title: "Target Achievement YTD",
    colorAttribute: "PERF_YTD_OPEN_rangecolor",
    valueAttribute: "PERF_YTD",
    unit: { value: "%" },
    minValue: { value: 0 },
    maxValue: { value: 100 },
    fields: [
      {
        title: "Actual YTD",
        attribute: "ACT_YTD_prettyvalue",
      },
      {
        title: "Target YTD",
        attribute: "TAR_YTD_prettyvalue",
      },
    ],
  };
  const gaugeSettings = getActiveSettings(
    element,
    System.views.executiveDashboardView.gaugeSettings
  );
  if (gaugeSettings) return Object.assign(defaultSettings, gaugeSettings);

  // no config for KPI gauge - use defaults
  return defaultSettings;
};

export const getCompletedWorkflowsViewSettings =
  (): TCompletedWorkflowsViewSettings => {
    const defaultSettings = {
      allowReactivation: false,
    };

    return Object.assign(defaultSettings, System.views.completedWorkflowsView);
  };

export const getMyTasksViewSettings = (): TMyTasksViewSettings => {
  const defaultSettings = {
    pagination: true,
    rowsPerPage: 30,
    showOnlyCurrentTasks: false,
  };

  return Object.assign(defaultSettings, System.views.myTasksView);
};

export const getWorkflowInputViewSettings = (): TWorkflowInputViewSettings => {
  const defaultSettings = {
    pagination: true,
    rowsPerPage: 6,
  };

  return Object.assign(defaultSettings, System.views.workflowInputView);
};

export const getKPIsAndInitiativesViewGaugeSettings = (
  element: ICardElementWithChildren
): IGaugeSettings => {
  const defaultSettings = {
    title: "Target Achievement YTD",
    colorAttribute: "rangecolor",
    valueAttribute: "PERF_YTD",
    unit: { value: "%" },
    minValue: { value: 0 },
    maxValue: { value: 100 },
    fields: [
      {
        title: "Actual YTD",
        attribute: "ACT_YTD_prettyvalue",
      },
      {
        title: "Target YTD",
        attribute: "TAR_YTD_prettyvalue",
      },
    ],
  };

  const gaugeSettings = getActiveSettings(
    element,
    System.views.kpisAndInitiativesView.gaugeSettings
  );
  if (gaugeSettings) return Object.assign(defaultSettings, gaugeSettings);

  // no config for KPI gauge - use defaults
  return defaultSettings;
};

export const getStrategyMapCardBackgroundImage = (
  element: ICardElementWithChildren
): string | undefined => {
  if (!System.views.strategyMapView.cardBackgroundImages) return undefined;
  let img = System.views.strategyMapView.cardBackgroundImages.find(
    (i) => i.parentSymbol && i.parentSymbol === element.parentSymbol
  );
  if (!img)
    img = System.views.strategyMapView.cardBackgroundImages.find(
      (i) => !i.parentSymbol
    );
  return img?.file;
};

export const getLanguages = (): string[] => {
  return System.settings.languages || ["en", "ar"];
};

export const getOverviewDashboardSettings =
  (): TOverviewDashboardViewSettings => {
    return System.views.overviewDashboardView;
  };

export const setRoutingFunction = (
  value: keyof typeof System.routing.typeToPageName
) => {
  System.routing.typeToPageName = value;
};
export const setIsDraftFunction = (
  value: typeof System.draftValuesHandling.isDraftStatus
) => {
  System.draftValuesHandling.isDraftStatus = value;
};
export const setDraftFilteredSeries = (
  value: typeof System.draftValuesHandling.getListOfDraftFilteredSeries
) => {
  System.draftValuesHandling.getListOfDraftFilteredSeries = value;
};
export const setDraftDisabledPages = (
  value: typeof System.draftValuesHandling.disabledPages
) => {
  System.draftValuesHandling.disabledPages = value;
};
export const setMapNavitems = (value: typeof System.mapNavitems) => {
  System.mapNavitems = value;
};
export const setMapQueryResults = (value: typeof System.mapQueryResults) => {
  System.mapQueryResults = value;
};
export const setDefaults = function <K extends keyof typeof System.settings>(
  key: K,
  value: typeof System.settings[K]
) {
  System.settings[key] = value;
};
export const setMetadata = (key: string, value: any) => {
  System.metadata[key] = value;
};
export const getMetadata = (key: string, value: any) => {
  return System.metadata[key];
};
